import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import PasswordInput from "../components/PasswordInput";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div className="hidden md:flex flex-col items-center justify-center">
          <ResmoMascot state={done ? "happy" : "thinking"} size={260} />
          <div className="font-display text-2xl mt-4 uppercase text-center">
            {done ? <>Back in<br />business.</> : <>New key,<br />same locker.</>}
          </div>
        </div>

        <div className="rm-card-static p-8 md:p-10">
          <div className="rm-tag mb-3">Password reset</div>

          {!token ? (
            <div data-testid="reset-no-token">
              <h1 className="font-display text-4xl uppercase mb-4">Missing reset link.</h1>
              <p className="text-sm mb-6" style={{ color: "#475569" }}>
                This page only works from the link in your reset email. Request a fresh one below.
              </p>
              <Link to="/forgot-password" className="rm-btn rm-btn-primary text-sm" data-testid="reset-request-new">
                Request a reset link
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <h1 className="font-display text-4xl uppercase mb-4">Password updated.</h1>
              <p className="text-sm mb-6" style={{ color: "#334155" }}>
                You're all set — sign in with your new password. Redirecting you now…
              </p>
              <Link to="/login" className="rm-btn rm-btn-primary text-sm" data-testid="reset-go-login">
                Sign in now
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl uppercase mb-2">Choose a new password.</h1>
              <p className="text-sm mb-6" style={{ color: "#475569" }}>
                Minimum 6 characters. Make it something Resmo-worthy.
              </p>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
                <div>
                  <label className="rm-label block mb-2">New password</label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    minLength={6}
                    autoComplete="new-password"
                    testId="reset-password-input"
                    ariaLabel="New password"
                  />
                </div>
                <div>
                  <label className="rm-label block mb-2">Confirm new password</label>
                  <PasswordInput
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    minLength={6}
                    autoComplete="new-password"
                    testId="reset-confirm-input"
                    ariaLabel="Confirm new password"
                  />
                </div>
                {error && (
                  <div className="font-mono-rm text-sm p-3 rounded-md"
                       style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
                       data-testid="reset-error">
                    {error}
                  </div>
                )}
                <button type="submit" disabled={loading || !password || !confirm}
                        className="rm-btn rm-btn-primary w-full disabled:opacity-50"
                        data-testid="reset-submit-btn">
                  <KeyRound size={16} /> {loading ? "Updating…" : "Set new password"}
                </button>
              </form>
              <div className="mt-6 text-sm font-mono-rm">
                <Link to="/login" className="font-bold underline inline-flex items-center gap-1"
                      data-testid="reset-back-to-login">
                  <ArrowLeft size={12} /> Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
